// src/pages/InscritosTodasCampanhas.js

import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import { format } from "date-fns";
import { FileSpreadsheet, Search, Trophy, Users } from "lucide-react";
import "./InscritosTodasCampanhas.css";

function InscritosTodasCampanhas() {
  const [inscritos, setInscritos] = useState([]);
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroCampanha, setFiltroCampanha] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);

  const itensPorPagina = 10;

  useEffect(() => {
    axios
      .get("/campanhas/todas-inscricoes")
      .then((res) => {
        setInscritos(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error("Erro ao carregar inscrições:", err);
      });
  }, []);

  useEffect(() => {
    setPaginaAtual(1);
  }, [filtroNome, filtroCampanha]);

  const inscritosFiltrados = useMemo(() => {
    return inscritos.filter((i) => {
      const nome = i.nome?.toLowerCase() || "";
      const titulo = i.titulo?.toLowerCase() || "";

      return (
        (!filtroNome || nome.includes(filtroNome.toLowerCase())) &&
        (!filtroCampanha || titulo.includes(filtroCampanha.toLowerCase()))
      );
    });
  }, [inscritos, filtroNome, filtroCampanha]);

  const inscritosOrdenados = useMemo(() => {
    return [...inscritosFiltrados].sort(
      (a, b) => (b.pontos_acumulados || 0) - (a.pontos_acumulados || 0)
    );
  }, [inscritosFiltrados]);

  const totalPaginas = Math.max(
    1,
    Math.ceil(inscritosOrdenados.length / itensPorPagina)
  );

  const inscritosPagina = inscritosOrdenados.slice(
    (paginaAtual - 1) * itensPorPagina,
    paginaAtual * itensPorPagina
  );

  const totalPontos = inscritosOrdenados.reduce(
    (acc, i) => acc + (i.pontos_acumulados || 0),
    0
  );

  const campanhasUnicas = useMemo(() => {
    return new Set(inscritos.map((i) => i.titulo).filter(Boolean)).size;
  }, [inscritos]);

  const maiorPontuacao = inscritosOrdenados.reduce(
    (maior, i) => Math.max(maior, i.pontos_acumulados || 0),
    0
  );

  const exportarExcel = () => {
    import("exceljs").then((ExcelJS) => {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Inscritos");

      ws.addRow([
        "Cliente",
        "Email",
        "Telefone",
        "Cidade",
        "Campanha",
        "Data Inscrição",
        "Objetivo",
        "Pontos Acumulados",
        "Recompensa",
      ]);

      inscritosOrdenados.forEach((i) => {
        ws.addRow([
          i.nome,
          i.email,
          i.telefone,
          i.cidade,
          i.titulo,
          format(new Date(i.data_inscricao), "dd/MM/yyyy"),
          i.pontos_objetivo,
          i.pontos_acumulados ?? 0,
          i.recompensa,
        ]);
      });

      wb.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        a.href = url;
        a.download = "inscricoes_todas_campanhas.xlsx";
        a.click();

        window.URL.revokeObjectURL(url);
      });
    });
  };

  const limparFiltros = () => {
    setFiltroNome("");
    setFiltroCampanha("");
  };

  const formatarNumero = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    });

  const formatarData = (data) => {
    if (!data) return "—";

    try {
      return format(new Date(data), "dd/MM/yyyy");
    } catch {
      return "—";
    }
  };

  return (
    <div className="inscritosTodasCampanhasAdmin-page">
      <div className="inscritosTodasCampanhasAdmin-topbar">
        <div>
          <span className="inscritosTodasCampanhasAdmin-kicker">
            Campanhas promocionais
          </span>

          <h2>Todos os Inscritos em Campanhas</h2>

          <p>
            Visualize inscrições de todas as campanhas, filtre por cliente ou
            campanha e exporte a base consolidada.
          </p>
        </div>

        <button
          type="button"
          className="inscritosTodasCampanhasAdmin-btn primary"
          onClick={exportarExcel}
        >
          <FileSpreadsheet size={16} />
          Exportar Excel
        </button>
      </div>

      <div className="inscritosTodasCampanhasAdmin-cards">
        <div className="inscritosTodasCampanhasAdmin-card">
          <span>Inscritos filtrados</span>
          <strong>{formatarNumero(inscritosOrdenados.length)}</strong>
        </div>

        <div className="inscritosTodasCampanhasAdmin-card success">
          <span>Total de pontos</span>
          <strong>{formatarNumero(totalPontos)}</strong>
        </div>

        <div className="inscritosTodasCampanhasAdmin-card info">
          <span>Campanhas</span>
          <strong>{formatarNumero(campanhasUnicas)}</strong>
        </div>

        <div className="inscritosTodasCampanhasAdmin-card warning">
          <span>Maior pontuação</span>
          <strong>{formatarNumero(maiorPontuacao)}</strong>
        </div>
      </div>

      <section className="inscritosTodasCampanhasAdmin-filtrosCard">
        <div className="inscritosTodasCampanhasAdmin-filtrosHeader">
          <div>
            <h3>Filtros</h3>
            <span>Refine a listagem por cliente ou nome da campanha.</span>
          </div>

          <Users size={18} />
        </div>

        <div className="inscritosTodasCampanhasAdmin-filtros">
          <div className="inscritosTodasCampanhasAdmin-filtro">
            <label>Cliente</label>
            <div className="inscritosTodasCampanhasAdmin-searchInput">
              <Search size={15} />
              <input
                type="text"
                placeholder="Filtrar por cliente"
                value={filtroNome}
                onChange={(e) => setFiltroNome(e.target.value)}
              />
            </div>
          </div>

          <div className="inscritosTodasCampanhasAdmin-filtro">
            <label>Campanha</label>
            <div className="inscritosTodasCampanhasAdmin-searchInput">
              <Search size={15} />
              <input
                type="text"
                placeholder="Filtrar por campanha"
                value={filtroCampanha}
                onChange={(e) => setFiltroCampanha(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="inscritosTodasCampanhasAdmin-filtrosActions">
          <button
            type="button"
            className="inscritosTodasCampanhasAdmin-btn secondary"
            onClick={limparFiltros}
          >
            Limpar filtros
          </button>

          <span>{formatarNumero(inscritosOrdenados.length)} resultado(s)</span>
        </div>
      </section>

      <section className="inscritosTodasCampanhasAdmin-tableCard">
        <div className="inscritosTodasCampanhasAdmin-tableHeader">
          <div>
            <h3>Lista consolidada</h3>
            <span>Ordenada automaticamente por maior pontuação acumulada.</span>
          </div>

          <Trophy size={18} />
        </div>

        <div className="inscritosTodasCampanhasAdmin-tableWrapper">
          <table className="inscritosTodasCampanhasAdmin-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Contato</th>
                <th>Local</th>
                <th>Campanha</th>
                <th>Inscrição</th>
                <th>Objetivo</th>
                <th>Pontos</th>
                <th>Recompensa</th>
              </tr>
            </thead>

            <tbody>
              {inscritosPagina.length === 0 ? (
                <tr>
                  <td colSpan="8" className="inscritosTodasCampanhasAdmin-empty">
                    Nenhum inscrito encontrado.
                  </td>
                </tr>
              ) : (
                inscritosPagina.map((i) => (
                  <tr key={i.id_inscricao}>
                    <td>
                      <div className="inscritosTodasCampanhasAdmin-cellMain">
                        <strong>{i.nome || "—"}</strong>
                        <span>ID inscrição #{i.id_inscricao || "—"}</span>
                      </div>
                    </td>

                    <td>
                      <div className="inscritosTodasCampanhasAdmin-cellMain">
                        <span>{i.email || "—"}</span>
                        <span>{i.telefone || "—"}</span>
                      </div>
                    </td>

                    <td>
                      <div className="inscritosTodasCampanhasAdmin-cellMain">
                        <span>{i.cidade || "—"}</span>
                      </div>
                    </td>

                    <td>
                      <div className="inscritosTodasCampanhasAdmin-cellMain">
                        <strong>{i.titulo || "—"}</strong>
                      </div>
                    </td>

                    <td>
                      <div className="inscritosTodasCampanhasAdmin-cellMain">
                        <span>{formatarData(i.data_inscricao)}</span>
                      </div>
                    </td>

                    <td>
                      <span className="inscritosTodasCampanhasAdmin-chip muted">
                        {formatarNumero(i.pontos_objetivo)} pts
                      </span>
                    </td>

                    <td>
                      <span className="inscritosTodasCampanhasAdmin-chip primary">
                        {formatarNumero(i.pontos_acumulados ?? 0)} pts
                      </span>
                    </td>

                    <td>
                      <div className="inscritosTodasCampanhasAdmin-cellMain recompensa">
                        <span>{i.recompensa || "—"}</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPaginas > 1 && (
          <div className="inscritosTodasCampanhasAdmin-paginacao">
            <button
              type="button"
              onClick={() => setPaginaAtual((prev) => Math.max(prev - 1, 1))}
              disabled={paginaAtual === 1}
            >
              Anterior
            </button>

            <span>
              Página {paginaAtual} de {totalPaginas}
            </span>

            <button
              type="button"
              onClick={() =>
                setPaginaAtual((prev) => Math.min(prev + 1, totalPaginas))
              }
              disabled={paginaAtual === totalPaginas}
            >
              Próxima
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

export default InscritosTodasCampanhas;